import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Check, UserPlus, Pencil, Trash2, Camera } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { useI18n } from "../i18n/I18nContext";
import { useProfile } from "../context/ProfileContext";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../components/ui/dialog";

const ROLE_LABEL = { responsavel: "role_responsavel", adulto: "role_adulto", cuidador: "role_cuidador" };
const LOG_PERMS = ["log_meals", "log_glucose", "log_doses", "log_measures", "correct_records"];
const PLANNED_PERMS = [
  "pg_history", "pg_logs", "pg_doses", "pg_correct",
  "pg_record", "pg_prescription", "pg_caregivers", "pg_export",
];
const MAX_RESP = 5;
const VIEW_ONLY = ["view_history", "view_analysis", "view_full_record"];
const ALL_PERMS = [
  "view_history", "view_analysis", "log_meals", "log_glucose", "log_doses", "correct_records",
  "view_full_record", "log_measures", "manage_prescription", "manage_caregivers", "export_data", "edit_profile",
];
const EDIT_PERMS = [
  ["log_meals", "Registar refeições"],
  ["log_glucose", "Registar glicemia"],
  ["log_doses", "Registar doses de insulina"],
  ["correct_records", "Corrigir registos"],
  ["log_measures", "Registar peso e altura"],
  ["edit_profile", "Editar perfil e foto"],
  ["manage_prescription", "Gerir alvo e prescrição"],
  ["export_data", "Exportar dados"],
  ["manage_caregivers", "Gerir responsáveis e cuidadores"],
];
const ACCESS = [
  { id: "view", label: "Só visualizar", desc: "Vê tudo, mas não pode registar, editar nem exportar nada." },
  { id: "full", label: "Editar tudo", desc: "Acesso total, incluindo gerir responsáveis." },
  { id: "custom", label: "Personalizado", desc: "Escolha o que pode fazer além de ver." },
];

function accessOf(perms) {
  if (!perms) return "full";
  if (ALL_PERMS.every((p) => perms.includes(p))) return "full";
  if (perms.every((p) => VIEW_ONLY.includes(p))) return "view";
  return "custom";
}

function AccessPicker({ access, setAccess, custom, setCustom }) {
  const toggle = (p) => setCustom((c) => (c.includes(p) ? c.filter((x) => x !== p) : [...c, p]));
  return (
    <div className="space-y-2" data-testid="resp-access">
      <Label>Tipo de acesso</Label>
      {ACCESS.map((a) => (
        <button type="button" key={a.id} onClick={() => setAccess(a.id)} data-testid={`access-${a.id}`}
          className={`w-full text-left rounded-xl border px-3 py-2.5 transition-colors ${access === a.id ? "border-[#1A73E8] bg-[#E8F1FB]" : "border-[#E3EFF9] bg-white"}`}>
          <span className="flex items-center gap-2">
            <span className={`h-4 w-4 rounded-full border-2 shrink-0 ${access === a.id ? "border-[#1A73E8] bg-[#1A73E8] shadow-[inset_0_0_0_2px_#fff]" : "border-[#9FB3C8]"}`} />
            <span className="text-sm font-semibold text-[#0D253F]">{a.label}</span>
          </span>
          <span className="block text-xs text-[#627D98] mt-0.5 pl-6">{a.desc}</span>
        </button>
      ))}
      {access === "custom" && (
        <div className="rounded-xl border border-[#E3EFF9] p-3 space-y-2" data-testid="custom-perms">
          <p className="text-xs text-[#627D98]">Ver histórico, análise e ficha está sempre incluído.</p>
          {EDIT_PERMS.map(([p, label]) => (
            <label key={p} className="flex items-center gap-2 text-sm text-[#0D253F] cursor-pointer">
              <Checkbox className="rounded-[4px]" checked={custom.includes(p)} onCheckedChange={() => toggle(p)} data-testid={`perm-${p}`} />
              {label}
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

function initials(name) {
  const words = (name || "").replace(/[^\p{L}\p{N} ]/gu, "").split(" ").filter(Boolean);
  if (!words.length) return "?";
  return words.slice(0, 2).map((w) => w[0].toUpperCase()).join("");
}

import { fileToAvatar } from "../lib/avatar";

function ageFrom(birth) {
  if (!birth) return null;
  const d = new Date(birth);
  if (isNaN(d.getTime())) return null;
  const now = new Date();
  let a = now.getFullYear() - d.getFullYear();
  const m = now.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < d.getDate())) a--;
  return a >= 0 && a < 130 ? a : null;
}

function ResponsavelDialog({ open, onOpenChange, record, familyId, onSaved }) {
  const { t } = useI18n();
  const isEdit = !!record?.id;
  const [name, setName] = useState(record?.name || "");
  const [email, setEmail] = useState(record?.email || "");
  const [avatar, setAvatar] = useState(record?.avatar_url || null);
  const [access, setAccess] = useState(accessOf(record?.permissions));
  const [custom, setCustom] = useState((record?.permissions || []).filter((p) => !VIEW_ONLY.includes(p)));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setName(record?.name || "");
    setEmail(record?.email || "");
    setAvatar(record?.avatar_url || null);
    setAccess(accessOf(record?.permissions));
    setCustom((record?.permissions || []).filter((p) => !VIEW_ONLY.includes(p)));
  }, [record]);

  async function pickAvatar(e) {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("Ficheiro inválido."); return; }
    try { setAvatar(await fileToAvatar(f)); } catch { toast.error("Não foi possível ler a imagem."); }
  }

  async function save() {
    if (!name.trim() || !email.trim()) { toast.error(t("name") + " / " + t("email")); return; }
    setSaving(true);
    const permissions = access === "full" ? ALL_PERMS : access === "view" ? VIEW_ONLY : [...VIEW_ONLY, ...custom];
    try {
      if (isEdit) {
        await api.patch(`/families/${familyId}/responsaveis/${record.id}`, { name, email, avatar, permissions });
        toast.success(t("resp_updated"));
      } else {
        await api.post(`/families/${familyId}/responsaveis`, { name, email, avatar, permissions });
        toast.success(t("resp_added"));
      }
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-head text-xl">{isEdit ? t("edit_responsavel") : t("new_responsavel")}</DialogTitle>
          <DialogDescription>{t("resp_login_hint")}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="flex items-center gap-4">
            <div className="relative shrink-0">
              {avatar ? (
                <img src={avatar} alt="" className="h-16 w-16 rounded-full object-cover border border-[#E3EFF9]" data-testid="resp-avatar" />
              ) : (
                <span className="h-16 w-16 rounded-full bg-[#1A73E8] text-white flex items-center justify-center text-2xl font-semibold">{(name || "?")[0]?.toUpperCase()}</span>
              )}
              <label className="absolute -bottom-1 -right-1 bg-white border border-[#E3EFF9] rounded-full p-1.5 shadow cursor-pointer" aria-label={t("photo_label")} data-testid="resp-photo-btn">
                <Camera size={14} className="text-[#1A73E8]" />
                <input type="file" accept="image/*" className="hidden" onChange={pickAvatar} />
              </label>
            </div>
            <p className="text-xs text-[#627D98]">{t("photo_label")}</p>
          </div>
          <div>
            <Label>{t("name")}</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="resp-name" />
          </div>
          <div>
            <Label>{t("email")}</Label>
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="resp-email" />
          </div>
          <AccessPicker access={access} setAccess={setAccess} custom={custom} setCustom={setCustom} />
          <Button onClick={save} disabled={saving} className="w-full h-11 bg-[#1A73E8] text-white font-semibold" data-testid="resp-save">
            {saving ? t("loading") : t("save")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function Family() {
  const { t } = useI18n();
  const { activeFamily, activeProfile, can, reload } = useProfile();
  const { user, updateMe, setUser } = useAuth();
  const [members, setMembers] = useState([]);
  const [dlg, setDlg] = useState(null); // null | {} (new) | member record

  const [myName, setMyName] = useState(user?.name || "");
  const [myEmail, setMyEmail] = useState(user?.email || "");
  const [myAvatar, setMyAvatar] = useState(user?.avatar_url || null);
  const [myBirth, setMyBirth] = useState(user?.birth_date || "");
  const [myDtype, setMyDtype] = useState(user?.diabetes_type || "");
  const [myWeight, setMyWeight] = useState(user?.weight_kg ?? "");
  const [myHeight, setMyHeight] = useState(user?.height_cm ?? "");
  const [savingMe, setSavingMe] = useState(false);

  useEffect(() => {
    setMyName(user?.name || "");
    setMyEmail(user?.email || "");
    setMyAvatar(user?.avatar_url || null);
    setMyBirth(user?.birth_date || "");
    setMyDtype(user?.diabetes_type || "");
    setMyWeight(user?.weight_kg ?? "");
    setMyHeight(user?.height_cm ?? "");
  }, [user]);

  async function pickMyAvatar(e) {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("Ficheiro inválido."); return; }
    try { setMyAvatar(await fileToAvatar(f)); } catch { toast.error("Não foi possível ler a imagem."); }
  }

  async function saveMyData() {
    if (!myName.trim() || !myEmail.trim()) { toast.error(t("name") + " / " + t("email")); return; }
    setSavingMe(true);
    try {
      const payload = { name: myName, email: myEmail, avatar: myAvatar, diabetes_type: myDtype, birth_date: myBirth };
      payload.weight_kg = (myWeight === "" || isNaN(Number(myWeight))) ? null : Number(myWeight);
      payload.height_cm = (myHeight === "" || isNaN(Number(myHeight))) ? null : Number(myHeight);
      await updateMe(payload);
      toast.success(t("profile_saved"));
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSavingMe(false);
    }
  }

  const familyId = activeFamily?.id;
  const monitoredName = activeProfile?.preferred_name || "";
  const firstName = monitoredName.split(" ")[0] || monitoredName;
  const respCount = members.filter((m) => m.role === "responsavel").length;

  const load = useCallback(async () => {
    if (!familyId) return;
    const m = await api.get(`/families/${familyId}/members`);
    setMembers(m.data);
  }, [familyId]);

  useEffect(() => { load(); }, [load]);

  async function onSaved() {
    setDlg(null);
    await load();
    // keep auth context fresh if the owner edited their own record
    try { const me = await api.get("/auth/me"); setUser(me.data); } catch { /* noop */ }
    await reload();
  }

  async function removeMember(m) {
    try {
      await api.delete(`/families/${familyId}/members/${m.id}`);
      toast.success(m.role === "responsavel" ? t("resp_removed") : t("remove"));
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  }

  function openAdd() {
    if (respCount >= MAX_RESP) { toast.error(t("resp_limit", { max: MAX_RESP })); return; }
    setDlg({});
  }

  function accessLabel(m) {
    const perms = m.permissions || [];
    if (m.role === "responsavel") {
      const a = accessOf(perms);
      return a === "full" ? "Editar tudo" : a === "view" ? "Só visualizar" : "Personalizado";
    }
    const canLog = perms.some((p) => LOG_PERMS.includes(p));
    return canLog ? t("access_log") : t("access_read");
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div>
          <p className="text-xs font-semibold tracking-[0.18em] text-[#9FB3C8] uppercase">
            {monitoredName} · {t("monitoring_space")}
          </p>
          <h1 className="font-head text-3xl sm:text-4xl font-bold text-[#0D253F] mt-2">{t("family_title")}</h1>
          <p className="text-[#627D98] mt-1">{t("family_sub")}</p>
        </div>
      </div>

      <div className="df-card p-4 sm:p-7" data-testid="mydata-card">
        <div className="mb-5">
          <h3 className="font-head text-xl font-bold text-[#0D253F]">{t("mydata_title")}</h3>
          <p className="text-sm text-[#627D98] mt-1">{t("mydata_sub")}</p>
        </div>
        <div className="flex flex-col sm:flex-row sm:items-start gap-5">
          <div className="relative shrink-0 w-fit">
            {myAvatar ? (
              <img src={myAvatar} alt="" className="h-20 w-20 rounded-full object-cover border border-[#E3EFF9]" data-testid="mydata-avatar" />
            ) : (
              <span className="h-20 w-20 rounded-full bg-[#1A73E8] text-white flex items-center justify-center text-3xl font-semibold" data-testid="mydata-avatar">{(myName || "?")[0]?.toUpperCase()}</span>
            )}
            <label className="absolute -bottom-1 -right-1 bg-white border border-[#E3EFF9] rounded-full p-2 shadow cursor-pointer" aria-label={t("photo_label")} data-testid="mydata-photo-btn">
              <Camera size={16} className="text-[#1A73E8]" />
              <input type="file" accept="image/*" className="hidden" onChange={pickMyAvatar} />
            </label>
          </div>
          <div className="flex-1 min-w-0 w-full space-y-4">
            <div className="grid sm:grid-cols-2 gap-4 [&>*]:min-w-0">
              <div>
                <Label>{t("name")}</Label>
                <Input value={myName} onChange={(e) => setMyName(e.target.value)} data-testid="mydata-name" />
              </div>
              <div>
                <Label>{t("email")}</Label>
                <Input type="email" value={myEmail} onChange={(e) => setMyEmail(e.target.value)} data-testid="mydata-email" />
              </div>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 [&>*]:min-w-0">
              <div>
                <Label>{t("diabetes_type")}</Label>
                <Input value={myDtype} onChange={(e) => setMyDtype(e.target.value)} placeholder="Tipo 1 / Tipo 2…" data-testid="mydata-dtype" />
              </div>
              <div>
                <Label>{t("birth_date")}</Label>
                <Input type="date" value={myBirth} onChange={(e) => setMyBirth(e.target.value)} data-testid="mydata-birth" />
                {ageFrom(myBirth) != null && (
                  <p className="text-xs text-[#627D98] mt-1" data-testid="mydata-age">{t("age")}: {ageFrom(myBirth)}</p>
                )}
              </div>
              <div>
                <Label>{t("weight")}</Label>
                <Input type="number" min="0" step="0.1" value={myWeight} onChange={(e) => setMyWeight(e.target.value)} data-testid="mydata-weight" />
              </div>
              <div>
                <Label>{t("height")}</Label>
                <Input type="number" min="0" step="0.1" value={myHeight} onChange={(e) => setMyHeight(e.target.value)} data-testid="mydata-height" />
              </div>
            </div>
          </div>
        </div>
        <Button onClick={saveMyData} disabled={savingMe} className="mt-5 bg-[#1A73E8] hover:bg-[#1557B0] text-white font-semibold rounded-full h-11 px-6" data-testid="mydata-save">
          {savingMe ? t("loading") : t("save")}
        </Button>
      </div>

      <div className="df-card p-4 sm:p-7" data-testid="members-card">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
          <h3 className="font-head text-xl font-bold text-[#0D253F]">{t("who_follows", { name: firstName })}</h3>
          {can("manage_caregivers") && (
            <Button
              onClick={openAdd}
              disabled={respCount >= MAX_RESP}
              className="bg-[#1A73E8] hover:bg-[#1557B0] text-white font-semibold shrink-0 h-10 px-4 rounded-full disabled:opacity-50"
              data-testid="add-responsavel-btn"
            >
              <UserPlus size={17} className="mr-1.5" /> {t("add_responsavel")} <span className="ml-1.5 opacity-80 text-xs">{respCount}/{MAX_RESP}</span>
            </Button>
          )}
        </div>
        <div className="divide-y divide-[#EDF3F9]">
          {members.map((m) => (
            <div key={m.id} className="flex flex-wrap sm:flex-nowrap items-center gap-x-4 gap-y-2 py-4 first:pt-0 last:pb-0" data-testid={`member-${m.id}`}>
              {m.avatar_url ? (
                <img src={m.avatar_url} alt="" className="h-12 w-12 rounded-full object-cover shrink-0" />
              ) : (
                <span className="h-12 w-12 rounded-full bg-[#D7E7FA] text-[#1A5DBD] flex items-center justify-center font-semibold shrink-0">{initials(m.name)}</span>
              )}
              <div className="flex-1 min-w-0">
                <div className="text-base font-semibold text-[#0D253F] truncate">{m.name}</div>
                <div className="text-sm text-[#627D98] truncate">{t(ROLE_LABEL[m.role] || "role_cuidador")} · {accessLabel(m)}</div>
                {m.email && <div className="text-xs text-[#9FB3C8] truncate" data-testid={`member-email-${m.id}`}>{m.email}</div>}
              </div>
              {m.role === "responsavel" ? (
                <div className="flex items-center gap-3 shrink-0 basis-full sm:basis-auto pl-16 sm:pl-0">
                  <span className="text-xs sm:text-sm font-semibold text-[#1A5DBD] bg-[#E8F1FB] rounded-full px-3 sm:px-4 py-1.5" data-testid={`role-badge-${m.id}`}>{t("role_responsavel")}</span>
                  {can("manage_caregivers") && (
                    <button onClick={() => setDlg(m)} className="text-[#1A73E8] hover:text-[#1557B0]" data-testid={`edit-responsavel-${m.id}`} aria-label={t("edit_responsavel")}><Pencil size={16} /></button>
                  )}
                  {can("manage_caregivers") && respCount > 1 && (
                    <button onClick={() => removeMember(m)} className="text-[#C5221F] hover:text-[#a51b18]" data-testid={`remove-member-${m.id}`} aria-label={t("remove")}><Trash2 size={16} /></button>
                  )}
                </div>
              ) : can("manage_caregivers") ? (
                <button onClick={() => removeMember(m)} className="text-sm font-semibold text-[#1A73E8] hover:text-[#1557B0] shrink-0" data-testid={`remove-member-${m.id}`}>{t("remove")}</button>
              ) : null}
            </div>
          ))}
        </div>
      </div>

      <div className="df-card p-4 sm:p-7" data-testid="permissions-card">
        <h3 className="font-head text-xl font-bold text-[#0D253F] mb-5">{t("planned_perms")}</h3>
        <div className="divide-y divide-[#EDF3F9]">
          {PLANNED_PERMS.map((p) => (
            <div key={p} className="flex items-center gap-4 py-4 first:pt-0 last:pb-0" data-testid={`planned-${p}`}>
              <span className="h-9 w-9 rounded-xl bg-[#E8F1FB] text-[#1A73E8] flex items-center justify-center shrink-0">
                <Check size={18} strokeWidth={2.5} />
              </span>
              <span className="text-base text-[#334E68]">{t(p)}</span>
            </div>
          ))}
        </div>
      </div>

      {dlg && (
        <ResponsavelDialog
          open={!!dlg}
          onOpenChange={(v) => !v && setDlg(null)}
          record={dlg.id ? dlg : null}
          familyId={familyId}
          onSaved={onSaved}
        />
      )}
    </div>
  );
}
